import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Popconfirm,
  Upload,
  Alert,
  Tooltip,
  InputNumber,
  Collapse,
  Switch
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  DownloadOutlined,
  ReloadOutlined,
  SyncOutlined,
  CloudSyncOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import * as subjectService from '../../services/subjectService';
import courseService from '../../services/courseService';
import { GradeSyncService } from '../../services/gradeSyncService';
import type { Subject, Course, SubjectCreateRequest, SubjectUpdateRequest } from '../../../../shared/types';

const { Option } = Select;

interface SubjectFormData {
  MonHocID: string;
  TenMonHoc: string;
  SoTC: number;
  HocKy: number;
  KhoaHocID: string;
  TongSoBuoi?: number; // Tổng số buổi học
  IsLocked?: boolean; // Khóa điểm danh môn học
}

const SubjectManagementPage: React.FC = () => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  const [form] = Form.useForm();
  const [syncForm] = Form.useForm();

  // Pagination and filters - keeping these for future use if needed
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState('');

  // Import states
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  
  // Grade sync states
  const [syncingGradesCourse, setSyncingGradesCourse] = useState<string | null>(null);
  const [syncingGradesSubject, setSyncingGradesSubject] = useState<string | null>(null);

  useEffect(() => {
    loadSubjects();
    loadCourses();
  }, [searchText, selectedCourseId]); // Removed currentPage, pageSize dependencies

  const loadSubjects = async () => {
    setLoading(true);
    try {
      // Check if user is authenticated
      const token = localStorage.getItem('quanly_truonghoc_token');
      if (!token) {
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        window.location.href = '/login';
        return;
      }

      // Load all subjects without pagination for collapse view
      const response = await subjectService.listSubjects({
        page: 1,
        limit: 9999, // Load all subjects
        search: searchText,
        khoaHocId: selectedCourseId
      });
      setSubjects(response.data);
      setTotal(response.pagination.totalItems);
    } catch (error: any) {
      console.error('Error loading subjects:', error);
      
      if (error.response?.status === 401) {
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        localStorage.removeItem('quanly_truonghoc_token');
        localStorage.removeItem('quanly_truonghoc_user');
        window.location.href = '/login';
      } else {
        const errorMsg = error.response?.data?.message || 'Không thể tải danh sách môn học';
        message.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      const response = await courseService.getCourses({ page: 1, limit: 1000 });
      setCourses(response.data);
    } catch (error: any) {
      console.error('Error loading courses:', error);
      
      if (error.response?.status === 401) {
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        localStorage.removeItem('quanly_truonghoc_token');
        localStorage.removeItem('quanly_truonghoc_user');
        window.location.href = '/login';
      } else {
        const errorMsg = error.response?.data?.message || 'Không thể tải danh sách khóa học';
        message.error(errorMsg);
      }
    }
  };

  const handleCreate = () => {
    setEditingSubject(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (subject: Subject) => {
    setEditingSubject(subject);
    form.setFieldsValue(subject);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await subjectService.deleteSubject(id);
      message.success('Xóa môn học thành công');
      loadSubjects();
    } catch (error: any) {
      console.error('Error deleting subject:', error);
      const errorMessage = error.response?.data?.error || 'Không thể xóa môn học';
      message.error(errorMessage);
    }
  };

  const handleToggleLock = async (id: string, isLocked: boolean) => {
    try {
      const result = await subjectService.lockSubjectAttendance(id, isLocked);
      message.success(result.message || (isLocked ? 'Đã khóa điểm danh môn học' : 'Đã mở khóa điểm danh môn học'));
      loadSubjects();
    } catch (error: any) {
      console.error('Error toggling lock:', error);
      const errorMessage = error.response?.data?.error || 'Không thể thay đổi trạng thái khóa';
      message.error(errorMessage);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formData: SubjectFormData = values;

      if (editingSubject) {
        const updateData: SubjectUpdateRequest = {
          TenMonHoc: formData.TenMonHoc,
          SoTC: formData.SoTC,
          HocKy: formData.HocKy,
          KhoaHocID: formData.KhoaHocID,
          TongSoBuoi: formData.TongSoBuoi,
          IsLocked: formData.IsLocked
        };
        await subjectService.updateSubject(editingSubject.MonHocID, updateData);
        message.success('Cập nhật môn học thành công');
      } else {
        const createData: SubjectCreateRequest = formData;
        await subjectService.createSubject(createData);
        message.success('Tạo môn học thành công');
      }

      setModalVisible(false);
      loadSubjects();
    } catch (error: any) {
      console.error('Error saving subject:', error);
      const errorMessage = error.response?.data?.error || 'Không thể lưu môn học';
      message.error(errorMessage);
    }
  };

  const handleSync = async () => {
    try {
      const values = await syncForm.validateFields();
      const result = await subjectService.syncSubjects({ KhoaHocID: values.KhoaHocID });
      
      message.success(
        `Đồng bộ thành công: ${result.added} môn học mới, ${result.updated} môn học cập nhật`
      );
      setSyncModalVisible(false);
      loadSubjects();
    } catch (error: any) {
      console.error('Error syncing subjects:', error);
      const errorMessage = error.response?.data?.error || 'Không thể đồng bộ môn học';
      message.error(errorMessage);
    }
  };

  const downloadTemplate = () => {
    subjectService.downloadSubjectTemplate();
  };

  // Handle sync grades for a course
  const handleSyncGradesByCourse = async (khoaHocId: string, courseName: string) => {
    try {
      setSyncingGradesCourse(khoaHocId);
      message.loading({ content: `Đang đồng bộ điểm cho ${courseName}...`, key: 'gradeSync', duration: 0 });
      
      const result = await GradeSyncService.syncGradesByKhoaHoc(khoaHocId);
      
      if (result.success && result.data) {
        message.success({
          content: `Đồng bộ điểm thành công: ${result.data.syncedRecords}/${result.data.totalRecords} bản ghi (${result.data.insertedRecords} mới, ${result.data.updatedRecords} cập nhật)`,
          key: 'gradeSync',
          duration: 5
        });
      } else {
        message.warning({
          content: result.message || 'Không tìm thấy dữ liệu điểm để đồng bộ',
          key: 'gradeSync',
          duration: 3
        });
      }
    } catch (error: any) {
      console.error('Error syncing grades:', error);
      message.error({
        content: error.message || 'Lỗi khi đồng bộ điểm',
        key: 'gradeSync'
      });
    } finally {
      setSyncingGradesCourse(null);
    }
  };

  // Handle sync grades for a subject
  const handleSyncGradesBySubject = async (khoaHocId: string, monHocId: string, tenMonHoc: string) => {
    try {
      setSyncingGradesSubject(monHocId);
      message.loading({ content: `Đang đồng bộ điểm môn ${tenMonHoc}...`, key: 'gradeSync', duration: 0 });
      
      const result = await GradeSyncService.syncGradesByMonHoc(khoaHocId, monHocId);
      
      if (result.success && result.data) {
        message.success({
          content: `Đồng bộ điểm môn ${tenMonHoc}: ${result.data.syncedRecords}/${result.data.totalRecords} bản ghi (${result.data.insertedRecords} mới, ${result.data.updatedRecords} cập nhật)`,
          key: 'gradeSync',
          duration: 5
        });
      } else {
        message.warning({
          content: result.message || 'Không tìm thấy dữ liệu điểm',
          key: 'gradeSync',
          duration: 3
        });
      }
    } catch (error: any) {
      console.error('Error syncing subject grades:', error);
      message.error({
        content: error.message || 'Lỗi khi đồng bộ điểm môn học',
        key: 'gradeSync'
      });
    } finally {
      setSyncingGradesSubject(null);
    }
  };

  const handleImport = async () => {
    if (fileList.length === 0) {
      message.error('Vui lòng chọn file Excel');
      return;
    }

    const file = fileList[0];
    try {
      // Read file and parse Excel data
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          // For now, let's show error that Excel parsing is not implemented
          message.error('Chức năng import Excel chưa được triển khai đầy đủ. Vui lòng sử dụng CSV template.');
        } catch (parseError) {
          message.error('Không thể đọc file Excel');
        }
      };
      reader.readAsArrayBuffer(file.originFileObj as File);
      
      setImportModalVisible(false);
      setFileList([]);
    } catch (error: any) {
      console.error('Error importing subjects:', error);
      const errorMessage = error.response?.data?.error || 'Không thể import môn học';
      message.error(errorMessage);
    }
  };

  const columns: ColumnsType<Subject> = [
    {
      title: 'Mã môn học',
      dataIndex: 'MonHocID',
      key: 'MonHocID',
      width: 150
    },
    {
      title: 'Tên môn học',
      dataIndex: 'TenMonHoc',
      key: 'TenMonHoc',
      ellipsis: true
    },
    {
      title: 'Số tín chỉ',
      dataIndex: 'SoTC',
      key: 'SoTC',
      width: 100,
      align: 'center'
    },
    {
      title: 'Học kỳ',
      dataIndex: 'HocKy',
      key: 'HocKy',
      width: 80,
      align: 'center'
    },
    {
      title: 'Tổng số buổi',
      dataIndex: 'TongSoBuoi',
      key: 'TongSoBuoi',
      width: 120,
      align: 'center',
      render: (tongSoBuoi: number) => (
        <span style={{ color: tongSoBuoi ? '#52c41a' : '#ff4d4f' }}>
          {tongSoBuoi || 'Chưa có'}
        </span>
      )
    },
    {
      title: 'Trạng thái điểm danh',
      dataIndex: 'IsLocked',
      key: 'IsLocked',
      width: 150,
      align: 'center',
      render: (isLocked: boolean) => (
        <span style={{ 
          color: isLocked ? '#ff4d4f' : '#52c41a',
          fontWeight: 'bold'
        }}>
          {isLocked ? '🔒 Đã khóa' : '🔓 Đang mở'}
        </span>
      )
    },
    {
      title: 'Khóa học',
      dataIndex: 'KhoaHocID',
      key: 'KhoaHocID',
      width: 150,
      render: (khoaHocId: string) => {
        const course = courses.find(c => c.KhoaHocID === khoaHocId);
        return course ? course.TenKhoaHoc : khoaHocId;
      }
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 200,
      align: 'center',
      render: (_, record) => (
        <Space>
          <Tooltip title="Sửa">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title={record.IsLocked ? "Mở khóa điểm danh" : "Khóa điểm danh"}
            description={record.IsLocked ? "Bạn có chắc chắn muốn mở khóa điểm danh cho môn học này?" : "Bạn có chắc chắn muốn khóa điểm danh cho môn học này?"}
            onConfirm={() => handleToggleLock(record.MonHocID, !record.IsLocked)}
            okText={record.IsLocked ? "Mở khóa" : "Khóa"}
            cancelText="Hủy"
          >
            <Tooltip title={record.IsLocked ? "Mở khóa điểm danh" : "Khóa điểm danh"}>
              <Button
                type={record.IsLocked ? "default" : "primary"}
                size="small"
                style={{ 
                  backgroundColor: record.IsLocked ? '#ff4d4f' : '#52c41a',
                  borderColor: record.IsLocked ? '#ff4d4f' : '#52c41a',
                  color: 'white'
                }}
              >
                {record.IsLocked ? '🔓' : '🔒'}
              </Button>
            </Tooltip>
          </Popconfirm>
          <Tooltip title="Đồng bộ điểm môn học">
            <Button
              type="default"
              size="small"
              icon={syncingGradesSubject === record.MonHocID ? <LoadingOutlined /> : <CloudSyncOutlined />}
              loading={syncingGradesSubject === record.MonHocID}
              onClick={() => handleSyncGradesBySubject(record.KhoaHocID, record.MonHocID, record.TenMonHoc)}
              style={{ 
                backgroundColor: '#1890ff',
                borderColor: '#1890ff',
                color: 'white'
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Xóa môn học"
            description="Bạn có chắc chắn muốn xóa môn học này?"
            onConfirm={() => handleDelete(record.MonHocID)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Tooltip title="Xóa">
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="📚 Quản lý môn học"
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Thêm môn học
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={downloadTemplate}
            >
              Tải template
            </Button>
            <Button
              icon={<UploadOutlined />}
              onClick={() => setImportModalVisible(true)}
            >
              Import Excel
            </Button>
            <Button
              icon={<SyncOutlined />}
              onClick={() => setSyncModalVisible(true)}
            >
              Đồng bộ
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadSubjects}
            >
              Làm mới
            </Button>
          </Space>
        }
      >
        {/* Filters */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Input.Search
              placeholder="Tìm kiếm môn học..."
              style={{ width: 300 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={loadSubjects}
              allowClear
            />
            <Select
              placeholder="Lọc theo khóa học"
              style={{ width: 200 }}
              value={selectedCourseId}
              onChange={setSelectedCourseId}
              allowClear
            >
              {courses.map(course => (
                <Option key={course.KhoaHocID} value={course.KhoaHocID}>
                  {course.TenKhoaHoc}
                </Option>
              ))}
            </Select>
          </Space>
        </div>

        {/* Group subjects by Course with Collapse */}
        <Collapse 
          ghost
          size="large"
          items={Object.entries(
            subjects.reduce((groups: Record<string, Record<number, Subject[]>>, subject) => {
              const course = courses.find(c => c.KhoaHocID === subject.KhoaHocID);
              const courseName = course ? course.TenKhoaHoc : subject.KhoaHocID;
              const hocKy = subject.HocKy || 1;
              
              if (!groups[courseName]) {
                groups[courseName] = {};
              }
              if (!groups[courseName][hocKy]) {
                groups[courseName][hocKy] = [];
              }
              groups[courseName][hocKy].push(subject);
              return groups;
            }, {})
          )
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([courseName, semesterGroups]) => {
            // Find KhoaHocID for this course name
            const course = courses.find(c => c.TenKhoaHoc === courseName);
            const khoaHocId = course?.KhoaHocID || '';
            
            return {
            key: courseName,
            label: (
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 600,
                color: '#1890ff',
                padding: '4px 0',
                textAlign: 'left',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}>
                <span>📚 Khóa học: {courseName}</span>
                {khoaHocId && (
                  <Tooltip title="Đồng bộ điểm cho tất cả môn học của khóa này">
                    <Button
                      type="primary"
                      size="small"
                      icon={syncingGradesCourse === khoaHocId ? <LoadingOutlined /> : <CloudSyncOutlined />}
                      loading={syncingGradesCourse === khoaHocId}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent collapse toggle
                        handleSyncGradesByCourse(khoaHocId, courseName);
                      }}
                      style={{ marginLeft: 16 }}
                    >
                      {syncingGradesCourse === khoaHocId ? 'Đang đồng bộ...' : 'Đồng bộ điểm'}
                    </Button>
                  </Tooltip>
                )}
              </div>
            ),
            children: (
              <div>
                {Object.entries(semesterGroups)
                  .sort(([a], [b]) => Number(a) - Number(b))
                  .map(([hocKy, subjectsInSemester]) => (
                    <div key={hocKy} style={{ marginBottom: 24 }}>
                      <h4 style={{ 
                        marginBottom: 16, 
                        fontSize: '14px', 
                        fontWeight: 600,
                        color: '#666',
                        backgroundColor: '#fafafa',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        border: '1px solid #e8e8e8',
                        textAlign: 'left'
                      }}>
                        Học kỳ {hocKy}
                      </h4>
                      <Table
                        columns={columns.filter(col => col.key !== 'HocKy' && col.key !== 'KhoaHocID')}
                        dataSource={subjectsInSemester}
                        rowKey="MonHocID"
                        loading={loading}
                        pagination={false}
                        size="small"
                        bordered
                      />
                    </div>
                  ))}
              </div>
            )
          };
          })
        }
        />

        {/* Show message if no subjects */}
        {subjects.length === 0 && !loading && (
          <div style={{ 
            textAlign: 'center', 
            padding: '50px 0',
            color: '#999' 
          }}>
            Không có môn học nào
          </div>
        )}

        {/* Summary info at the bottom */}
        {total > 0 && (
          <div style={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 24,
            padding: '16px 0',
            borderTop: '1px solid #f0f0f0'
          }}>
            <div style={{ 
              fontSize: '14px',
              color: '#666'
            }}>
              📊 Tổng số {total} môn học
            </div>
          </div>
        )}
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingSubject ? 'Sửa môn học' : 'Thêm môn học'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="Lưu"
        cancelText="Hủy"
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            SoTC: 3,
            HocKy: 1
          }}
        >
          <Form.Item
            name="MonHocID"
            label="Mã môn học"
            rules={[{ required: true, message: 'Vui lòng nhập mã môn học' }]}
          >
            <Input
              placeholder="Nhập mã môn học"
              disabled={!!editingSubject}
            />
          </Form.Item>

          <Form.Item
            name="TenMonHoc"
            label="Tên môn học"
            rules={[{ required: true, message: 'Vui lòng nhập tên môn học' }]}
          >
            <Input placeholder="Nhập tên môn học" />
          </Form.Item>

          <Form.Item
            name="SoTC"
            label="Số tín chỉ"
            rules={[{ required: true, message: 'Vui lòng nhập số tín chỉ' }]}
          >
            <InputNumber
              min={1}
              max={10}
              placeholder="Số tín chỉ"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="HocKy"
            label="Học kỳ"
            rules={[{ required: true, message: 'Vui lòng chọn học kỳ' }]}
          >
            <InputNumber
              min={1}
              max={8}
              placeholder="Học kỳ"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="KhoaHocID"
            label="Khóa học"
            rules={[{ required: true, message: 'Vui lòng chọn khóa học' }]}
          >
            <Select placeholder="Chọn khóa học">
              {courses.map(course => (
                <Option key={course.KhoaHocID} value={course.KhoaHocID}>
                  {course.TenKhoaHoc}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="TongSoBuoi"
            label="Tổng số buổi học"
            rules={[
              { 
                type: 'number', 
                min: 1, 
                max: 100, 
                message: 'Số buổi học phải từ 1 đến 100' 
              }
            ]}
          >
            <InputNumber
              min={1}
              max={100}
              placeholder="Nhập tổng số buổi học"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="IsLocked"
            label="Khóa điểm danh"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Đã khóa"
              unCheckedChildren="Đang mở"
              defaultChecked={false}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Import Modal */}
      <Modal
        title="Import môn học từ Excel"
        open={importModalVisible}
        onOk={handleImport}
        onCancel={() => {
          setImportModalVisible(false);
          setFileList([]);
        }}
        okText="Import"
        cancelText="Hủy"
      >
        <Alert
          message="Hướng dẫn import"
          description={
            <div>
              <p>1. Tải file template Excel bằng nút "Tải template"</p>
              <p>2. Điền thông tin môn học vào file template</p>
              <p>3. Chọn file đã điền và nhấn "Import"</p>
              <p><strong>Lưu ý:</strong> Mã môn học không được trùng với dữ liệu hiện có</p>
            </div>
          }
          type="info"
          style={{ marginBottom: 16 }}
        />
        
        <Upload
          fileList={fileList}
          onChange={({ fileList }) => setFileList(fileList)}
          beforeUpload={() => false}
          accept=".xlsx,.xls"
          maxCount={1}
        >
          <Button icon={<UploadOutlined />}>Chọn file Excel</Button>
        </Upload>
      </Modal>

      {/* Sync Modal */}
      <Modal
        title="Đồng bộ môn học từ cơ sở dữ liệu phụ"
        open={syncModalVisible}
        onOk={handleSync}
        onCancel={() => setSyncModalVisible(false)}
        okText="Đồng bộ"
        cancelText="Hủy"
      >
        <Alert
          message="Thông tin đồng bộ"
          description="Chức năng này sẽ lấy dữ liệu môn học từ cơ sở dữ liệu phụ và cập nhật vào hệ thống chính"
          type="warning"
          style={{ marginBottom: 16 }}
        />
        
        <Form form={syncForm} layout="vertical">
          <Form.Item
            name="KhoaHocID"
            label="Khóa học"
            rules={[{ required: true, message: 'Vui lòng chọn khóa học để đồng bộ' }]}
          >
            <Select placeholder="Chọn khóa học">
              {courses.map(course => (
                <Option key={course.KhoaHocID} value={course.KhoaHocID}>
                  {course.TenKhoaHoc}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SubjectManagementPage;
